import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const RAW_BACKEND = process.env.REACT_APP_BACKEND_URL;
const BACKEND_OK = (() => { try { return ['http:', 'https:'].includes(new URL(RAW_BACKEND).protocol); } catch { return false; } })();
const API = BACKEND_OK ? `${RAW_BACKEND.replace(/\/$/, '')}/api` : '/api';

export const AuthProvider = ({ children }) => {
  if (!BACKEND_OK) {
    throw new Error('REACT_APP_BACKEND_URL mancante o invalido. Configura il backend e rigenera il frontend.');
  }
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    if (!token) { setUser(null); setLoading(false); return; }
    setLoading(true);
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal })
      .then(response => { if (active) setUser(response.data); })
      .catch(error => {
        if (!active) return;
        if ([401, 403].includes(error.response?.status)) {
          localStorage.removeItem('token'); setToken(null); setUser(null);
        }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    // If resident is pending admin approval, do NOT auto-login
    if (userData?.approval_status === 'pending') {
      return { user: userData, pending: true };
    }
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return { user: userData, pending: false };
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isAdminCondo: user?.role === 'admin_condo',
    isCommercial: user?.role === 'commercial',
    isCompany: user?.role === 'company',
    isUser: user?.role === 'user',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default AuthContext;
